import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiFileText, FiUploadCloud, FiTrash2, FiCheckCircle, FiAlertCircle,
  FiLoader, FiZap, FiMapPin, FiEye, FiPlay, FiExternalLink, FiEdit3
} from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { confirmToast } from '../../lib/confirmToast';
import { regularisationTotaleEra } from '../../utils/remboursementEra';

// ==============================================================================
// ✅ FIX #4 — UploadModal extrait HORS de TaxesDetail (évite le remontage)
// ==============================================================================
const UploadModal = ({
  isOpen,
  onClose,
  modalContext,
  isUploadingFacture,
  existingDocs,
  onUpload,
  onView,
  onDelete,
}) => {
  // ✅ État local drag & drop
  const [isDragging, setIsDragging] = useState(false);

  if (!isOpen || !modalContext) return null;

  const { pdl, year } = modalContext;

  // ✅ Handlers drag & drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploadingFacture) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Évite le flicker quand la souris passe sur un enfant
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isUploadingFacture) return;

    const dropped = Array.from(e.dataTransfer.files).filter(f =>
      ['application/pdf', 'image/jpeg', 'image/png'].includes(f.type)
    );

    if (dropped.length === 0) {
      return; // fichiers ignorés silencieusement (mauvais type)
    }

    onUpload(dropped);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg p-3 max-w-md w-full border border-gray-200 shadow-lg">

        {/* En-tête */}
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-xs font-semibold text-gray-800">Upload Factures</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-sm leading-none px-1"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        {/* Contexte PDL / Année */}
        <div className="mb-2 p-2 bg-orange-50 rounded-md border border-orange-200">
          <p className="text-[10px] font-semibold text-orange-800 leading-tight">
            {pdl?.site_name || `PDL ${pdl?.pdl_number}`}
          </p>
          <p className="text-[9px] text-orange-700">Année fiscale : {year}</p>
        </div>

        {/* ✅ Zone drag & drop + clic */}
        <label
          htmlFor="file-upload"
          className={`w-full border-2 border-dashed rounded-md p-4 flex flex-col justify-center items-center text-center transition-all cursor-pointer select-none ${
            isUploadingFacture
              ? 'border-gray-200 bg-gray-50 cursor-default'
              : isDragging
                ? 'border-orange-500 bg-orange-50 scale-[1.01]'
                : 'border-orange-300 bg-white hover:border-orange-400 hover:bg-orange-50/30'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {isUploadingFacture ? (
            <>
              <FiLoader className="mx-auto h-6 w-6 text-orange-500 animate-spin" />
              <span className="mt-1.5 block text-[10px] font-semibold text-gray-600">
                Envoi en cours...
              </span>
            </>
          ) : isDragging ? (
            <>
              <FiUploadCloud className="mx-auto h-7 w-7 text-orange-500" />
              <span className="mt-1.5 block text-[11px] font-bold text-orange-600">
                Déposez les fichiers ici
              </span>
            </>
          ) : (
            <>
              <FiUploadCloud className="mx-auto h-6 w-6 text-gray-400" />
              <span className="mt-1.5 block text-[10px] font-semibold text-gray-700">
                Glisser-déposer ou{' '}
                <span className="text-orange-500 underline">cliquer pour sélectionner</span>
              </span>
              <p className="text-[9px] text-gray-400 mt-0.5">PDF, PNG, JPG • 10 MB max</p>
            </>
          )}
        </label>

        <input
          id="file-upload"
          type="file"
          className="hidden"
          multiple
          accept="application/pdf,image/jpeg,image/png"
          onChange={(e) => onUpload(Array.from(e.target.files))}
          disabled={isUploadingFacture}
        />

        {/* Liste des documents existants */}
        {existingDocs.length > 0 && (
          <div className="mt-3">
            <h4 className="text-[10px] font-semibold text-gray-700 mb-1">
              Documents existants ({existingDocs.length})
            </h4>
            <ul className="space-y-1.5 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50">
              {existingDocs.map((file) => (
                <li
                  key={file.id}
                  className="flex items-center justify-between text-[10px] px-2 py-1.5 bg-white rounded-md border border-gray-100"
                >
                  <span
                    className="truncate flex items-center gap-1.5 font-mono text-[9px] text-gray-700"
                    title={file.original_file_name}
                  >
                    <FiFileText className="text-gray-500 w-3.5 h-3.5 flex-shrink-0" />
                    {file.original_file_name}
                  </span>
                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    <button
                      onClick={() => onView(file.id)}
                      className="w-6 h-6 inline-flex items-center justify-center rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                      title="Voir"
                    >
                      <FiEye className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onDelete(file.id)}
                      className="w-6 h-6 inline-flex items-center justify-center rounded bg-red-50 text-red-600 hover:bg-red-100"
                      title="Supprimer"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {existingDocs.length === 0 && (
          <p className="mt-2 text-center text-[9px] text-gray-400">
            Aucune facture importée pour ce PDL / cette année.
          </p>
        )}
      </div>
    </div>
  );
};

// ==============================================================================
// COMPOSANT PRINCIPAL
// ==============================================================================
const TaxesDetail = ({ clientData, session }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [pdls, setPdls] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [analysisResults, setAnalysisResults] = useState({});
  const [isUploadingFacture, setIsUploadingFacture] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState(null);
  const [analyzingStates, setAnalyzingStates] = useState({});
  const [cerfaWarnings, setCerfaWarnings] = useState({});

  const fiscalYearOptions = [2023, 2024, 2025, 2026, 2027];
  const [premierBilanConfig, setPremierBilanConfig] = useState({});
  const [manualModeFlags, setManualModeFlags] = useState({}); // clé: "pdlId-year" → boolean

  // ==============================================================================
  // CHARGEMENT DES PDL
  // ==============================================================================
  const loadPdls = useCallback(async () => {
    const accountId = clientData?.accountId;
    if (!accountId) return;

    try {
      const { data, error } = await supabase
        .from('pdl')
        .select('*')
        .eq('account_id', accountId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPdls(data || []);
    } catch (error) {
      console.error('❌ Erreur chargement PDL:', error);
      toast.error('Impossible de charger les points de livraison.');
      setPdls([]);
    }
  }, [clientData?.accountId]);

  // ==============================================================================
  // CHARGEMENT DES DOCUMENTS
  // ✅ FIX #2 — Normalisation de doc.year en Number pour éviter "2024" === 2024 → false
  // ==============================================================================
  const loadDocuments = useCallback(async () => {
    const accountId = clientData?.accountId;
    if (!accountId || !session?.access_token) return;

    try {
      const { data } = await axios.get(
        `/api/cspe/documents/uploads-s3?accountId=${accountId}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (data.success) {
        const filteredDocs = data.documents
          .filter(doc =>
            doc.document_type_id === 'factures_electricite' ||
            doc.document_type_id === 'facture_electricite'
          )
          // ✅ Normalisation des types pour éviter les comparaisons string vs number
          .map(doc => ({
            ...doc,
            year: doc.year != null ? Number(doc.year) : null,
            pdl_id: doc.pdl_id ?? null,
          }));

        setDocuments(filteredDocs);
      }
    } catch (error) {
      console.error('❌ Erreur chargement documents:', error.response?.data || error);
      if (error.response?.status === 401) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else {
        toast.error('Impossible de charger les documents.');
      }
    }
  }, [clientData?.accountId, session?.access_token]);

  // ==============================================================================
  // CHARGEMENT DES RÉSULTATS D'ANALYSE
  // ==============================================================================
  const loadAnalysisResults = useCallback(async () => {
    const accountId = clientData?.accountId;
    if (!accountId || !session?.access_token) return;

    try {
      const { data: analysisRes } = await axios.get(
        `/api/cspe/documents/analysis-summary?accountId=${accountId}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (analysisRes.success) {
        const resultsMap = analysisRes.data.reduce((acc, result) => {
          let year;
          if (result.annee_fiscale) {
            year = result.annee_fiscale;
          } else {
            const dateFin = new Date(result.exercice_end_date);
            year = dateFin.getMonth() < 6
              ? dateFin.getFullYear() - 1
              : dateFin.getFullYear();
          }
          acc[`${result.pdl_id}-${year}`] = result;
          return acc;
        }, {});

        setAnalysisResults(resultsMap);
      }
    } catch (error) {
      console.error('❌ Erreur chargement analyses:', error.response?.data || error);
      if (error.response?.status !== 401) {
        toast.error('Impossible de charger les résultats d\'analyse.');
      }
    }
  }, [clientData?.accountId, session?.access_token]);

  // ==============================================================================
  // CHARGEMENT DES FLAGS MODE MANUEL
  // ==============================================================================
  const loadManualModeFlags = useCallback(async () => {
    const accountId = clientData?.accountId;
    if (!accountId) return;

    try {
      const { data, error } = await supabase
        .from('cspe_manual_mode')
        .select('pdl_id, year, enabled')
        .eq('account_id', accountId);

      if (error) throw error;

      const flagsMap = (data || []).reduce((acc, row) => {
        if (row.enabled) acc[`${row.pdl_id}-${row.year}`] = true;
        return acc;
      }, {});

      setManualModeFlags(flagsMap);
    } catch (error) {
      console.error('Erreur chargement mode manuel:', error);
    }
  }, [clientData?.accountId]);

  const toggleManualMode = useCallback(async (pdlId, year, enabled) => {
    const accountId = clientData?.accountId;
    if (!accountId) return;

    const key = `${pdlId}-${year}`;
    setManualModeFlags(prev => ({ ...prev, [key]: enabled }));

    try {
      const { error } = await supabase
        .from('cspe_manual_mode')
        .upsert(
          { account_id: accountId, pdl_id: pdlId, year, enabled },
          { onConflict: 'account_id,pdl_id,year' }
        );

      if (error) throw error;
      toast.success(enabled ? 'Mode manuel activé' : 'Mode manuel désactivé');
    } catch (error) {
      console.error('Erreur toggle mode manuel:', error);
      toast.error('Erreur lors du changement de mode');
      setManualModeFlags(prev => ({ ...prev, [key]: !enabled }));
    }
  }, [clientData?.accountId]);

  // ==============================================================================
  // CHARGEMENT INITIAL
  // ==============================================================================
  useEffect(() => {
    const loadInitialData = async () => {
      if (!clientData?.accountId) return;
      setIsLoading(true);
      await Promise.all([loadPdls(), loadDocuments(), loadAnalysisResults(), loadManualModeFlags()]);
      setIsLoading(false);
    };
    loadInitialData();
  }, [clientData?.accountId, loadPdls, loadDocuments, loadAnalysisResults, loadManualModeFlags]);

  // ==============================================================================
  // UPLOAD FICHIER
  // ✅ FIX #3 — setIsModalOpen retiré d'ici ; géré dans handleFactureUpload
  // ==============================================================================
  const uploadFileToS3 = async (file, type, context) => {
    const { pdl, year } = context;
    const toastId = toast.loading('Préparation de l\'envoi...');
    const accountId = clientData?.accountId;

    if (!accountId) {
      toast.error('ID du compte manquant.', { id: toastId });
      throw new Error('accountId manquant');
    }

    // ÉTAPE 1 : URL presignée
    const { data: presignData } = await axios.post(
      '/api/cspe/documents/request-upload-url',
      { fileName: file.name, accountId },
      { headers: { Authorization: `Bearer ${session.access_token}` } }
    );
    if (!presignData.success) throw new Error('URL d\'upload non obtenue');

    const { uploadUrl, s3Key } = presignData.data;
    toast.loading('Envoi en cours...', { id: toastId });

    // ÉTAPE 2 : Upload S3
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': file.type },
    });

    toast.loading('Finalisation...', { id: toastId });

    // ÉTAPE 3 : Confirmation backend
    await axios.post(
      '/api/cspe/documents/confirm-upload',
      {
        s3Key,
        originalFileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        type,
        year,
        pdl_id: pdl?.id || null,
        accountId,
      },
      { headers: { Authorization: `Bearer ${session.access_token}` } }
    );

    toast.success(`${file.name} envoyé !`, { id: toastId });
    // ✅ Pas de setIsModalOpen ici — c'est handleFactureUpload qui contrôle la modal
  };

  // ✅ FIX #3 — loadDocuments appelé UNE SEULE FOIS après la boucle + finally sur isUploadingFacture
  const handleFactureUpload = async (files, context) => {
    if (!files || files.length === 0) return;
    setIsUploadingFacture(true);

    try {
      for (const file of files) {
        await uploadFileToS3(file, 'factures_electricite', context);
      }
      // Rechargement unique après tous les uploads
      await loadDocuments();
      // ✅ La modal reste ouverte → l'utilisateur voit ses documents ajoutés
    } catch (error) {
      console.error('❌ Erreur upload:', error);
      toast.error(error.response?.data?.message || 'Upload échoué');
    } finally {
      setIsUploadingFacture(false); // ✅ Toujours réinitialisé
    }
  };

  // ==============================================================================
  // ACTIONS DOCUMENTS
  // ==============================================================================
  const handleViewDocument = async (docId) => {
    const toastId = toast.loading('Ouverture...');
    try {
      const { data } = await axios.get(
        `/api/cspe/documents/uploads-s3/${docId}/download`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      if (data.success && data.url) {
        window.open(data.url, '_blank');
        toast.dismiss(toastId);
      } else {
        throw new Error('URL non reçue');
      }
    } catch (error) {
      toast.error('Impossible d\'ouvrir le document', { id: toastId });
    }
  };

  const handleDeleteDocument = async (docId) => {
  const doc = documents.find(d => d.id === docId);
  
  const confirmed = await confirmToast({
    title: 'Supprimer ce document ?',
    subtitle: doc?.original_file_name
      ? `"${doc.original_file_name}" sera définitivement supprimé.`
      : 'Cette action est irréversible.',
    confirmLabel: 'Supprimer',
    variant: 'danger',
  });
  if (!confirmed) return;

  const toastId = toast.loading('Suppression...');
  try {
    await axios.delete(`/api/cspe/documents/uploads-s3/${docId}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    toast.success('Document supprimé', { id: toastId });
    await loadDocuments();
  } catch (error) {
    toast.error('Suppression échouée', { id: toastId });
  }
};

  // ==============================================================================
  // ANALYSE CSPE
  // ==============================================================================
  const handleLaunchAnalysis = useCallback(async (pdlId, year) => {
    const key = `${pdlId}-${year}`;
    setAnalyzingStates(prev => ({ ...prev, [key]: true }));

    const accountId = clientData?.accountId;
    if (!accountId) {
      toast.error('ID compte introuvable');
      setAnalyzingStates(prev => ({ ...prev, [key]: false }));
      return;
    }

    const documentsToProcess = documents.filter(
      doc => doc.pdl_id === pdlId && doc.year === year // ✅ types normalisés → comparaison fiable
    );

    if (documentsToProcess.length === 0) {
      toast.error('Aucun document à analyser');
      setAnalyzingStates(prev => ({ ...prev, [key]: false }));
      return;
    }

    const toastId = toast.loading('Démarrage analyse...');

    try {
      // ÉTAPE 1 : Validation
      toast.loading(`Étape 1/3 : Validation (${documentsToProcess.length} doc)...`, { id: toastId });
      await Promise.all(
        documentsToProcess.map(doc =>
          axios.post(
            `/api/cspe/documents/uploads-s3/${doc.id}/validate`,
            {},
            { headers: { Authorization: `Bearer ${session.access_token}` } }
          )
        )
      );

      // ÉTAPE 2 : Identification fournisseurs
      toast.loading('Étape 2/3 : Identification fournisseurs...', { id: toastId });
      await Promise.all(
        documentsToProcess.map(doc =>
          axios.post(
            '/api/cspe/ocr/identify-provider',
            {
              documentId: doc.id,
              storagePath: `s3://${doc.s3_bucket}/${doc.s3_key}`,
            },
            { headers: { Authorization: `Bearer ${session.access_token}` } }
          )
        )
      );

      // Rafraîchir les documents après identification
      const updatedDocsResponse = await axios.get(
        `/api/cspe/documents/uploads-s3?accountId=${accountId}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      const updatedDocuments = updatedDocsResponse.data.documents.filter(
        doc => doc.pdl_id === pdlId && Number(doc.year) === year // ✅ normalisation inline
      );

      // ÉTAPE 3 : OCR (extraction des données)
      toast.loading(`Étape 3/3 : OCR (${updatedDocuments.length} doc)...`, { id: toastId });
      await Promise.all(
        updatedDocuments.map(doc => {
          const payload = {
            documentId: doc.id,
            storagePath: `s3://${doc.s3_bucket}/${doc.s3_key}`,
            providerName: doc.fournisseur_electricite,
            accountId,
            year,
            pdl_id: pdlId,
          };
          if (!payload.providerName || payload.providerName === 'UNKNOWN') {
            throw new Error(`Fournisseur non identifié : ${doc.original_file_name}`);
          }
          return axios.post('/api/cspe/ocr/process-stored-invoice', payload, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
        })
      );

      toast.success('Extraction des données terminée !', { id: toastId });

      await Promise.all([loadDocuments(), loadAnalysisResults()]);
    } catch (error) {
      console.error('❌ Erreur analyse:', error);
      toast.error(error.response?.data?.message || 'Analyse échouée', { id: toastId });
    } finally {
      setAnalyzingStates(prev => ({ ...prev, [key]: false }));
    }
  }, [clientData?.accountId, documents, session, loadDocuments, loadAnalysisResults]);

  // ==============================================================================
  // HELPERS
  // ==============================================================================
  const getPdlDocuments = (pdlId, year) =>
    documents.filter(doc => doc.pdl_id === pdlId && doc.year === year); // ✅ types normalisés

  const openModal = (pdl, year) => {
    setModalContext({ type: 'facture', pdl, year });
    setIsModalOpen(true);
  };

  const navigateToCspeAnalysis = (pdlId, year, result) => {
    const pdl = pdls.find(p => p.id === pdlId);
    navigate(`/dashboard/admin/analyse-cspe/${clientData.accountId}`, {
      state: { pdl, year, analysisResult: result },
    });
  };

  // ==============================================================================
  // ÉTATS DE CHARGEMENT / ERREUR
  // ==============================================================================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <FiLoader className="animate-spin h-5 w-5 text-orange-500 mr-2" />
        <span className="text-[10px] text-gray-600">Chargement…</span>
      </div>
    );
  }

  if (!clientData?.accountId) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
        <FiAlertCircle className="mx-auto h-7 w-7 text-red-500 mb-1.5" />
        <h3 className="text-xs font-semibold text-red-900">Erreur</h3>
        <p className="text-[10px] text-red-700 mt-1">Données client non chargées</p>
      </div>
    );
  }

  if (pdls.length === 0) {
    return (
      <div className="space-y-2">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-3 text-white">
          <div className="flex items-center gap-2">
            <FiFileText className="h-5 w-5" />
            <div>
              <h1 className="text-sm font-bold leading-tight">Analyse CSPE</h1>
              <p className="text-[10px] text-orange-100 leading-tight">
                Optimisation des taxes énergétiques pour {clientData.name}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
          <FiAlertCircle className="mx-auto h-7 w-7 text-yellow-500 mb-1.5" />
          <h3 className="text-xs font-semibold text-yellow-900">Aucun point de livraison</h3>
          <p className="text-[10px] text-yellow-700 mt-1">Ce compte ne possède aucun PDL.</p>
          <p className="text-[9px] text-yellow-700/80 mt-1">Ajoutez des PDL dans l'onglet "Courtage".</p>
        </div>
      </div>
    );
  }

  // ==============================================================================
  // RENDU PRINCIPAL
  // ==============================================================================
  return (
    <div className="space-y-2">

      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg px-3 py-2 text-white">
        <div className="flex items-center gap-2">
          <FiFileText className="h-5 w-5" />
          <div className="min-w-0">
            <h1 className="text-sm font-bold leading-tight">Analyse CSPE</h1>
            <p className="text-[10px] text-orange-100 leading-tight truncate">
              Optimisation des taxes énergétiques pour {clientData.name}
            </p>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200">
          <h2 className="text-xs font-semibold text-gray-900 flex items-center">
            <FiZap className="mr-1.5 text-blue-500 w-3.5 h-3.5" />
            Factures par PDL
          </h2>
          <p className="text-[10px] text-gray-600 mt-0.5">Cliquez sur une cellule pour gérer les factures.</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[220px]">
                  Point de Livraison
                </th>
                {fiscalYearOptions.map(year => (
                  <th
                    key={year}
                    className="px-2 py-2 text-center text-[9px] font-semibold text-gray-500 uppercase tracking-wider min-w-[92px]"
                  >
                    {year}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-100">
              {pdls.map(pdl => (
                <tr key={pdl.id} className="hover:bg-gray-50/50">

                  {/* Colonne PDL */}
                  <td className="px-2 py-2 whitespace-nowrap sticky left-0 bg-white z-10 border-r border-gray-200">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                        <FiZap className="h-3 w-3 text-blue-600" />
                      </div>
                      <div className="ml-2 min-w-0">
                        <div className="text-[10px] font-semibold text-gray-900 leading-tight truncate">
                          {pdl.site_name}
                        </div>
                        <div className="text-[9px] text-gray-500 font-mono leading-tight truncate">
                          {pdl.pdl_number}
                        </div>
                        <div className="text-[9px] text-gray-400 flex items-center gap-1 leading-tight">
                          <FiMapPin className="w-3 h-3" />
                          <span className="truncate">
                            {pdl.address?.city} • {pdl.contract_power} kVA
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Colonnes années */}
                  {fiscalYearOptions.map(year => {
                    const analysisKey = `${pdl.id}-${year}`;
                    const result = analysisResults[analysisKey];
                    const isAnalyzing = analyzingStates[analysisKey];
                    const docsForCell = getPdlDocuments(pdl.id, year);
                    const docsCount = docsForCell.length;
                    const hasDocs = docsCount > 0;
                    const isManualMode = !!manualModeFlags[analysisKey];
                    // OCR effectué = au moins un doc identifié (fournisseur trouvé)
                    const isExtracted = hasDocs && docsForCell.some(d => d.fournisseur_electricite && d.fournisseur_electricite !== 'UNKNOWN');

                    return (
                      <td key={year} className="px-2 py-2 whitespace-nowrap text-center align-top">
                        <div className="flex flex-col items-center gap-1.5">

                          {/* ✅ FIX #1 — Bouton gestion factures TOUJOURS visible (avant ET après analyse) */}
                          <button
                            onClick={() => openModal(pdl, year)}
                            className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold transition-colors ${
                              hasDocs
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : isManualMode
                                  ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                                  : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {hasDocs ? (
                              <>
                                <FiCheckCircle className="mr-1 h-3.5 w-3.5" />
                                {docsCount} facture{docsCount > 1 ? 's' : ''}
                              </>
                            ) : isManualMode ? (
                              <>
                                <FiEdit3 className="mr-1 h-3.5 w-3.5" />
                                Mode manuel
                              </>
                            ) : (
                              <>
                                <FiAlertCircle className="mr-1 h-3.5 w-3.5" />
                                À fournir
                              </>
                            )}
                          </button>

                          {/* Résultat d'analyse ou extraction OCR terminée */}
                          {(result || isExtracted) ? (
                            <>
                              {result ? (
                                <div className="text-center">
                                  <div className="text-[12px] font-bold text-green-600 leading-tight">
                                    {Math.abs(regularisationTotaleEra(result)).toLocaleString(
                                      'fr-FR', { style: 'currency', currency: 'EUR' }
                                    )}
                                  </div>
                                  <div className="text-[9px] text-green-700 font-semibold leading-tight">
                                    économies
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <div className="text-[10px] font-semibold text-blue-600 leading-tight">
                                    Extraction OK
                                  </div>
                                  <div className="text-[9px] text-gray-500 leading-tight">
                                    Continuer dans Détails
                                  </div>
                                </div>
                              )}

                              {/* Warnings TICFE */}
                              {cerfaWarnings[analysisKey]?.length > 0 && (
                                <div className="w-full bg-orange-50 border border-orange-200 rounded p-1.5 text-left">
                                  <p className="text-[9px] font-semibold text-orange-800 flex items-center gap-1 mb-1">
                                    <FiAlertCircle className="w-3 h-3 flex-shrink-0" />
                                    {cerfaWarnings[analysisKey].length} facture(s) à vérifier
                                  </p>
                                  {cerfaWarnings[analysisKey].map((w, i) => {
                                    const doc = documents.find(d => d.id === w.document_id);
                                    return (
                                      <button
                                        key={i}
                                        onClick={() => handleViewDocument(w.document_id)}
                                        className="block text-[8px] text-orange-700 underline truncate max-w-full mb-0.5"
                                        title={w.parser_message || w.extraction_status}
                                      >
                                        {doc?.original_file_name || `Facture #${i + 1}`}
                                      </button>
                                    );
                                  })}
                                  <button
                                    onClick={() =>
                                      setCerfaWarnings(prev => {
                                        const next = { ...prev };
                                        delete next[analysisKey];
                                        return next;
                                      })
                                    }
                                    className="text-[8px] text-orange-400 hover:text-orange-600 mt-0.5"
                                  >
                                    Ignorer
                                  </button>
                                </div>
                              )}

                              <button
                                onClick={() => navigateToCspeAnalysis(pdl.id, year, result)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
                              >
                                <FiExternalLink className="w-3 h-3" />
                                Détails
                              </button>

                              {isAnalyzing ? (
                                <div className="flex items-center gap-1 text-[10px] text-orange-600">
                                  <FiLoader className="animate-spin w-3 h-3" />
                                  Analyse…
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleLaunchAnalysis(pdl.id, year)}
                                  disabled={!hasDocs}
                                  className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md transition-colors ${
                                    hasDocs
                                      ? 'text-orange-700 hover:text-orange-900 hover:bg-orange-50'
                                      : 'text-gray-400 cursor-not-allowed'
                                  }`}
                                  title={hasDocs ? 'Relancer l\'analyse' : 'Aucune facture à analyser'}
                                >
                                  <FiPlay className="w-3 h-3" />
                                  Relancer
                                </button>
                              )}
                            </>
                          ) : (
                            /* Pas encore analysé */
                            <>
                              {isAnalyzing ? (
                                <div className="flex items-center gap-1 text-[10px] text-blue-600">
                                  <FiLoader className="animate-spin w-3 h-3" />
                                  Analyse…
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleLaunchAnalysis(pdl.id, year)}
                                  disabled={!hasDocs}
                                  className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md transition-colors ${
                                    hasDocs
                                      ? 'text-blue-700 hover:text-blue-900 hover:bg-blue-50'
                                      : 'text-gray-400 cursor-not-allowed'
                                  }`}
                                >
                                  <FiPlay className="w-3 h-3" />
                                  Analyser
                                </button>
                              )}

                              {/* Bouton saisie manuelle — visible quand mode manuel activé */}
                              {isManualMode && (
                                <button
                                  onClick={() => navigateToCspeAnalysis(pdl.id, year, null)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-md transition-colors"
                                >
                                  <FiEdit3 className="w-3 h-3" />
                                  Saisie manuelle
                                </button>
                              )}
                            </>
                          )}
                        <div className="mt-1 w-full space-y-1">
                            {/* Toggle Mode manuel */}
                            <button
                              type="button"
                              role="switch"
                              aria-checked={isManualMode}
                              onClick={() => toggleManualMode(pdl.id, year, !isManualMode)}
                              className="flex items-center gap-1.5 justify-center group"
                            >
                              <span
                                className={`relative inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                                  isManualMode ? 'bg-purple-600' : 'bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    isManualMode ? 'translate-x-3' : 'translate-x-0'
                                  }`}
                                />
                              </span>
                              <span className={`text-[9px] font-medium ${isManualMode ? 'text-purple-800' : 'text-gray-500'}`}>
                                Manuel
                              </span>
                            </button>

                            {/* Toggle 1er bilan */}
                            <button
                              type="button"
                              role="switch"
                              aria-checked={!!premierBilanConfig[`${pdl.id}-${year}`]?.enabled}
                              onClick={() => {
                                const key = `${pdl.id}-${year}`;
                                const next = !premierBilanConfig[key]?.enabled;
                                setPremierBilanConfig((prev) => ({
                                  ...prev,
                                  [key]: {
                                    ...prev[key],
                                    enabled: next,
                                    startDate: next ? prev[key]?.startDate || '' : '',
                                  },
                                }));
                              }}
                              className="flex items-center gap-1.5 justify-center group"
                            >
                              <span
                                className={`relative inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                                  premierBilanConfig[`${pdl.id}-${year}`]?.enabled ? 'bg-amber-500' : 'bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    premierBilanConfig[`${pdl.id}-${year}`]?.enabled ? 'translate-x-3' : 'translate-x-0'
                                  }`}
                                />
                              </span>
                              <span className={`text-[9px] font-medium ${premierBilanConfig[`${pdl.id}-${year}`]?.enabled ? 'text-amber-800' : 'text-gray-500'}`}>
                                1er bilan
                              </span>
                            </button>

                            {premierBilanConfig[`${pdl.id}-${year}`]?.enabled && (
                              <input
                                type="date"
                                value={premierBilanConfig[`${pdl.id}-${year}`]?.startDate || ''}
                                onChange={(e) =>
                                  setPremierBilanConfig((prev) => ({
                                    ...prev,
                                    [`${pdl.id}-${year}`]: {
                                      ...prev[`${pdl.id}-${year}`],
                                      startDate: e.target.value,
                                    },
                                  }))
                                }
                                className="mt-1 w-full text-[9px] border border-amber-300 rounded px-1 py-0.5 bg-white"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ✅ FIX #4 — UploadModal rendu en dehors du tableau, avec props explicites */}
      <UploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        modalContext={modalContext}
        isUploadingFacture={isUploadingFacture}
        existingDocs={
          modalContext
            ? getPdlDocuments(modalContext.pdl?.id, modalContext.year)
            : []
        }
        onUpload={(files) => handleFactureUpload(files, modalContext)}
        onView={handleViewDocument}
        onDelete={handleDeleteDocument}
      />

    </div>
  );
};

export default TaxesDetail;
